// Identity & address verification (MeitY advisory for matrimonial websites): the user uploads
// proofs, an admin checks them, and the profile shows a Verified badge. Documents are private.
import React, { useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import { Paper, Stack, Typography, Chip, Button, TextField, MenuItem, Alert, CircularProgress, Box } from '@mui/material';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { MY_VERIFICATION_QUERY } from '../graphql/queries';
import { uploadIdentityDocument } from '../utils/media';

const DOC_TYPES = [
  ['aadhaar', 'Aadhaar card (mask the first 8 digits)'], ['passport', 'Passport'], ['voter_id', 'Voter ID'],
  ['driving_licence', 'Driving licence'], ['pan', 'PAN card (identity only)'], ['other', 'Other government ID'],
];
const STATUS = {
  unverified: ['Not verified', 'default'], pending: ['Being checked', 'warning'], verified: ['Verified', 'success'], rejected: ['Not accepted — please upload again', 'error'],
};

export default function VerificationCard({ sx }) {
  const { data, refetch } = useQuery(MY_VERIFICATION_QUERY, { fetchPolicy: 'cache-and-network' });
  const [docType, setDocType] = useState('aadhaar');
  const [purpose, setPurpose] = useState('identity');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const fileInput = useRef(null);
  const v = data?.myVerification;
  if (!v) return null;
  const [label, color] = STATUS[v.status] || STATUS.unverified;

  const upload = async (file) => {
    if (!file) return;
    setBusy(true); setMsg(null);
    try {
      await uploadIdentityDocument(file, { docType, purpose });
      setMsg({ severity: 'success', text: 'Uploaded. We will check it and let you know.' });
      refetch();
    } catch (e) { setMsg({ severity: 'error', text: e.message }); }
    setBusy(false);
  };

  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, ...sx }}>
      <Stack direction="row" spacing={1} alignItems="center" mb={1}>
        <VerifiedUserIcon color={v.status === 'verified' ? 'success' : 'action'} />
        <Typography variant="h6" fontWeight={700}>Identity verification</Typography>
        <Chip size="small" color={color} label={label} />
      </Stack>
      <Typography variant="body2" color="text.secondary" mb={1.5}>
        Upload an identity proof and an address proof. Once we check them, your profile shows a <b>Verified</b> badge, which helps other
        families trust it. Documents are stored privately, seen only by our verification team, and never shown to other members.
      </Typography>
      {v.status === 'rejected' && v.note && <Alert severity="warning" sx={{ mb: 1.5 }}>{v.note}</Alert>}
      {msg && <Alert severity={msg.severity} sx={{ mb: 1.5 }} onClose={() => setMsg(null)}>{msg.text}</Alert>}
      {v.documents.length > 0 && (
        <Stack spacing={0.5} mb={1.5}>
          {v.documents.map(d => (
            <Typography key={d.id} variant="body2">
              {d.purpose === 'identity' ? 'Identity proof' : 'Address proof'}: {DOC_TYPES.find(t => t[0] === d.docType)?.[1] || d.docType} · {d.fileName}
              {' '}<Chip size="small" variant="outlined" label={d.status} />
            </Typography>
          ))}
        </Stack>
      )}
      {v.status !== 'verified' && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
          <TextField select size="small" label="Proof of" value={purpose} onChange={e => setPurpose(e.target.value)} sx={{ minWidth: 140 }}>
            <MenuItem value="identity">Identity</MenuItem>
            <MenuItem value="address">Address</MenuItem>
          </TextField>
          <TextField select size="small" label="Document" value={docType} onChange={e => setDocType(e.target.value)} sx={{ minWidth: 260 }}>
            {DOC_TYPES.map(([k, l]) => <MenuItem key={k} value={k}>{l}</MenuItem>)}
          </TextField>
          <Box>
            <input ref={fileInput} type="file" accept="application/pdf,.pdf,image/jpeg,image/png,image/webp" hidden onChange={e => { upload(e.target.files?.[0]); e.target.value = ''; }} />
            <Button variant="contained" size="small" disabled={busy} onClick={() => fileInput.current?.click()}
              startIcon={busy ? <CircularProgress size={14} /> : <UploadFileIcon />}>
              {busy ? 'Uploading…' : 'Upload'}
            </Button>
          </Box>
        </Stack>
      )}
    </Paper>
  );
}
